import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, UserPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { formatApiError } from "../api";

const RegisterPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register(name.trim(), email.trim().toLowerCase(), password);
      setDone(true);
      toast.success(t.auth.register_pending);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col" data-testid="register-page">
      <div className="px-6 md:px-12 py-6 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white/55 hover:text-[#FFDE00]"
          data-testid="register-home-link"
        >
          <ArrowLeft size={14} /> {t.auth.home}
        </Link>
        <div className="font-display text-2xl tracking-tight">
          TEAM ASTANA<span className="text-[#FFDE00]">.</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        {done ? (
          <div className="max-w-md text-center" data-testid="register-success">
            <CheckCircle2 size={48} className="text-[#FFDE00] mx-auto mb-6" />
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter">
              {t.auth.register_pending.split(".")[0]}.
            </h1>
            <p className="mt-4 text-white/65">{t.auth.register_pending}</p>
            <button
              onClick={() => navigate("/login")}
              className="btn-outline mt-8"
              data-testid="register-done-to-login"
            >
              {t.auth.to_login}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
            <div className="kicker mb-4">/register</div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-10">
              {t.auth.register_title}
            </h1>

            <div className="space-y-7">
              <div>
                <label className="text-[10px] tracking-widest uppercase text-white/45">
                  {t.auth.name}
                </label>
                <input
                  type="text"
                  required
                  minLength={2}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="field-input"
                  data-testid="register-name"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-white/45">
                  {t.auth.email}
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="field-input"
                  data-testid="register-email"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-white/45">
                  {t.auth.password}
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="field-input"
                  data-testid="register-password"
                  autoComplete="new-password"
                />
              </div>
            </div>

            {error && (
              <div
                className="mt-6 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary mt-8 w-full"
              data-testid="register-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> {t.auth.registering}
                </>
              ) : (
                <>
                  <UserPlus size={16} /> {t.auth.register_btn}
                </>
              )}
            </button>

            <div className="mt-8 text-center text-sm text-white/55">
              <Link to="/login" className="hover:text-[#FFDE00]" data-testid="register-to-login">
                {t.auth.to_login}
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
